import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Input from '../Input';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%'
  },
  codeInputRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: '5px',
    width: '100%',
    maxWidth: 360,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 8,
    marginBottom: 8
  },
  codeInputField: {
    width: '100%',
    '&.MuiOutlinedInput-root': {
      height: 64,
      borderRadius: 12,
      backgroundColor: '#F4F4F4',
      padding: 0
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#022A5C'
    },
    '& .MuiOutlinedInput-input': {
      textAlign: 'center',
      padding: '0 !important',
      height: '64px',
      fontSize: 28,
      fontWeight: 600,
      color: '#212121',
      boxSizing: 'border-box'
    }
  },
  error: {
    textAlign: 'center'
  }
}));

const OTP = ({
  value,
  error,
  helperText,
  onChange
}) => {
  const classes = useStyles();
  const codeInputRefs = useRef([]);

  const focusCodeInput = index => {
    const input = codeInputRefs.current[index];

    if(input) {
      input.focus();
      input.select();
    }
  }

  const updateCodeValue = nextCode => {
    onChange(nextCode.slice(0, 6));
  }

  const handleCodeDigitChange = (index, nextValue) => {
    const digits = nextValue.replace(/\D/g, '');
    const currentDigits = (value || '').padEnd(6, ' ').split('').slice(0, 6);

    if(!digits) {
      currentDigits[index] = '';
      updateCodeValue(currentDigits.join('').replace(/\s/g, ''));
      return;
    }

    currentDigits[index] = digits.slice(-1);

    updateCodeValue(currentDigits.join('').replace(/\s/g, ''));

    if(index < 5) {
      focusCodeInput(index + 1);
    }
  }

  const handleCodePaste = (event, index) => {
    event.preventDefault();

    const pastedDigits = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6 - index);

    if(!pastedDigits) {
      return;
    }

    const currentDigits = (value || '').padEnd(6, ' ').split('').slice(0, 6);

    pastedDigits.split('').forEach((digit, digitIndex) => {
      currentDigits[index + digitIndex] = digit;
    });

    updateCodeValue(currentDigits.join('').replace(/\s/g, ''));

    const nextIndex = Math.min(index + pastedDigits.length, 5);
    focusCodeInput(nextIndex);
  }

  const handleCodeKeyDown = (event, index) => {
    if(event.key == 'Backspace' && !value?.[index] && index > 0) {
      const nextCode = (value || '').slice(0, index - 1);
      updateCodeValue(nextCode);
      focusCodeInput(index - 1);
    }
  }

  return(
    <div className={classes.root}>
      <div className={classes.codeInputRow}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Input
            key={index}
            withoutFullWidth
            name={`code-${index}`}
            type="text"
            value={value?.[index] || ''}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 1,
              onPaste: event => handleCodePaste(event, index),
              onKeyDown: event => handleCodeKeyDown(event, index)
            }}
            styleInput={classes.codeInputField}
            onChange={event => handleCodeDigitChange(index, event.target.value)}
            inputRef={input => {
              codeInputRefs.current[index] = input;
            }}
            error={!!error}
          />
        ))}
      </div>
      {!!helperText && (
        <Typography variant="body2" color="error" className={classes.error}>
          {helperText}
        </Typography>
      )}
    </div>
  );
}

OTP.defaultProps = {
  value: '',
  error: '',
  helperText: ''
};

OTP.propTypes = {
  value: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  helperText: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

export default OTP;